import { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Link, useNavigate } from "react-router-dom";
import { AuthLayout } from "../auth/AuthLayout";
import { Button, TextField, Checkbox } from "@/design/components";
import { TierSelect } from "./TierSelect";
import { register as registerOrg } from "@/api/auth";
import { decodeSocialSignup } from "@/api/oauth";
import { SocialRegisterScreen } from "./SocialRegisterScreen";
import { SocialButtons } from "../auth/SocialButtons";
import { ApiError } from "@/api/client";
import { TERMS_VERSION, LEGAL_TERMS_URL, LEGAL_PRIVACY_URL } from "@/config";
import authStyles from "../auth/AuthForm.module.css";
import styles from "./RegisterScreen.module.css";

// Mirrors api registerSchema exactly: orgName, email, password(min8), adminName?,
// tier, acceptedTerms(must be true). termsVersion is injected at submit.
const registerSchema = z.object({
  orgName: z.string().trim().min(1, "Organization name is required"),
  adminName: z.string().trim().min(1, "Your name is required"),
  email: z.string().trim().email("Enter a valid work email"),
  password: z.string().min(8, "At least 8 characters"),
  tier: z.enum(["free", "small", "medium", "large", "xlarge"]),
  // Mirrors the API T&C gate: acceptedTerms must be exactly true.
  acceptedTerms: z.boolean().refine((v) => v === true, {
    error: "You must accept the Terms to continue",
  }),
});
type RegisterValues = z.infer<typeof registerSchema>;

export function RegisterScreen() {
  // A social-login redirect lands here carrying a signed `#social` handoff token → finish
  // sign-up WITHOUT a password. Decoded once at mount (display only; the API re-verifies
  // the signature on submit). No token → the normal email/password form (unchanged).
  const [social] = useState(() => {
    try {
      const t = new URLSearchParams(window.location.hash.replace(/^#/, "")).get("social");
      if (!t) return null;
      const prefill = decodeSocialSignup(t);
      return prefill ? { token: t, prefill } : null;
    } catch {
      return null;
    }
  });
  if (social) return <SocialRegisterScreen token={social.token} prefill={social.prefill} />;
  return <EmailRegisterScreen />;
}

function EmailRegisterScreen() {
  const navigate = useNavigate();
  const [formError, setFormError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm<RegisterValues>({
    resolver: zodResolver(registerSchema),
    defaultValues: { tier: "free", acceptedTerms: false },
  });

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      const res = await registerOrg({
        orgName: values.orgName,
        adminName: values.adminName,
        email: values.email,
        password: values.password,
        tier: values.tier,
        acceptedTerms: true,
        termsVersion: TERMS_VERSION,
      });
      // Hand the one-time Org_ID to the reveal screen via router state (never the
      // URL — it must not land in history/logs). replace:true so Back can't
      // return to a re-submittable form for an org that now exists.
      navigate("/welcome", {
        replace: true,
        state: { orgId: res.orgId, orgName: res.org.name, email: res.admin.email },
      });
    } catch (err) {
      setFormError(
        err instanceof ApiError
          ? err.message
          : "Something went wrong creating your organization. Please try again.",
      );
    }
  });

  return (
    <AuthLayout>
      <header className={authStyles.head}>
        <p className="u-eyebrow">Get started</p>
        <h2 className={authStyles.title}>Create your organization</h2>
      </header>

      <form className={authStyles.form} onSubmit={onSubmit} noValidate>
        {formError && <div className={authStyles.formError} role="alert">{formError}</div>}

        <TextField
          label="Organization name"
          placeholder="Acme Corp"
          required
          error={errors.orgName?.message}
          {...register("orgName")}
        />

        <div className={authStyles.row2}>
          <TextField
            label="Your name"
            placeholder="Alex Rivera"
            required
            error={errors.adminName?.message}
            {...register("adminName")}
          />
          <TextField
            label="Work email"
            type="email"
            autoComplete="email"
            placeholder="alex@acme.com"
            required
            error={errors.email?.message}
            {...register("email")}
          />
        </div>

        <TextField
          label="Password"
          type="password"
          autoComplete="new-password"
          placeholder="At least 8 characters"
          required
          hint="You'll sign in with this email and password — as the org owner."
          error={errors.password?.message}
          {...register("password")}
        />

        <div className={styles.tierBlock}>
          <p className={styles.tierLabel}>Starting plan</p>
          <Controller
            control={control}
            name="tier"
            render={({ field }) => <TierSelect value={field.value} onChange={field.onChange} />}
          />
          <p className={styles.tierHint}>Start free — upgrade anytime from billing. No card needed for Free.</p>
        </div>

        <Controller
          control={control}
          name="acceptedTerms"
          render={({ field }) => (
            <Checkbox
              checked={!!field.value}
              onChange={(e) => field.onChange(e.target.checked)}
              onBlur={field.onBlur}
              error={errors.acceptedTerms?.message}
              label={
                <>
                  I have read and accept the{" "}
                  <a href={LEGAL_TERMS_URL} target="_blank" rel="noreferrer">
                    Terms &amp; Conditions
                  </a>{" "}
                  and{" "}
                  <a href={LEGAL_PRIVACY_URL} target="_blank" rel="noreferrer">
                    Privacy Policy
                  </a>
                  .
                </>
              }
            />
          )}
        />

        <Button type="submit" size="lg" fullWidth loading={isSubmitting}>
          Create organization
        </Button>
      </form>

      <SocialButtons verb="Continue" />

      <p className={authStyles.alt}>
        Already have an account? <Link to="/login">Sign in</Link>
      </p>
    </AuthLayout>
  );
}
